import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signIn } from '@/lib/auth';

export default function AdminLoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error: authError } = await signIn(email.trim(), password);
    setLoading(false);
    if (authError) {
      setError(t('adm1.login_failed'));
    } else {
      navigate('/admin/dashboard');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-100 px-4">
      <div className="w-full max-w-md">
        <div className="bg-background-50 rounded-2xl border border-background-200/70 p-8 md:p-10">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-500/10">
              <i className="ri-shield-keyhole-line text-2xl text-primary-600"></i>
            </div>
            <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">{t('adm1.login_title')}</h1>
            <p className="mt-1 text-xs text-foreground-500 font-body">{t('adm1.login_subtitle')}</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-xs tracking-[0.1em] uppercase font-label text-foreground-600 mb-2">
                {t('adm1.label_email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder={t('admx.login_email_placeholder')}
                autoFocus
                required
              />
            </div>
            <div>
              <label className="block text-xs tracking-[0.1em] uppercase font-label text-foreground-600 mb-2">
                {t('adm1.label_password')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  className="w-full px-4 py-3 pr-12 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder={showPassword ? t('adm1.ph_enter_password') : '••••••••'}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                  title={showPassword ? t('adm1.pw_hide') : t('adm1.pw_show')}
                >
                  <i className={`ri-${showPassword ? 'eye-off' : 'eye'}-line text-base`}></i>
                </button>
              </div>
              {error && (
                <p className="mt-2 text-xs text-red-500 font-body">{error}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl bg-primary-500 text-white text-sm tracking-[0.1em] uppercase font-label hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
            >
              {loading ? t('adm1.login_loading') : t('adm1.login_submit')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}